import { ProgressBarState } from '@aus-platform/design-system';
import { isEmpty } from 'lodash';
import { FileType } from 'src/shared/api/layers/enums';

type FileUploadStatus = {
  isFileIdle: boolean;
  isFileUploading: boolean;
  isFileProcessing: boolean;
  isFileUploaded: boolean;
  isFileUploadError: boolean;
  isFileUploadCancelled: boolean;
};

type UploadIdentifier = {
  uploadId: string;
  fileId: string;
};

type CompleteUploadFunction = (data: {
  upload_id: string;
  fileId: string;
  filetype: string;
  parts: { ETag: string; PartNumber: number }[];
}) => void;

// Constants.
const MIN_CHUNK_SIZE = 5 * 1024 * 1024; // 5MB (S3 minimum)
const LARGE_FILE_THRESHOLD = 5 * 100 * 1024 * 1024; // 500MB
const LARGE_FILE_CHUNK_SIZE = 20 * 1024 * 1024; // 20MB
const MAX_PARTS = 10000;

/**
 * Returns the file upload status based on the progress state.
 *
 * @param {ProgressBarState} progress - The current progress state of the file upload.
 * @returns {FileUploadStatus} The status of the file upload.
 */
export const getFileUploadStatus = (
  progress: ProgressBarState,
): FileUploadStatus => {
  const fileUploadStatus: FileUploadStatus = {
    isFileIdle: progress === ProgressBarState.Initial,
    isFileUploading:
      progress >= ProgressBarState.Started &&
      progress < ProgressBarState.Uploaded,
    isFileProcessing: progress === ProgressBarState.Processing,
    isFileUploaded: progress === ProgressBarState.Completed,
    isFileUploadError: progress === ProgressBarState.Error,
    isFileUploadCancelled: progress === ProgressBarState.Cancelled,
  };

  return fileUploadStatus;
};

/**
 * Calculates the configuration for file upload chunks based on the file size.
 * @param fileSize - The size of the file to be uploaded.
 * @returns An object containing the chunk size and the number of parts.
 */
export const calculateChunkConfig = (fileSize: number) => {
  // For files smaller than 5MB, upload in a single chunk.
  if (fileSize <= MIN_CHUNK_SIZE) {
    return {
      chunkSize: fileSize,
      partsCount: 1,
    };
  }

  // For files less than 500MB.
  if (fileSize < LARGE_FILE_THRESHOLD) {
    // Calculate how many complete 5MB chunks we can have.
    const completeChunks = Math.floor(fileSize / MIN_CHUNK_SIZE);
    const remainder = fileSize % MIN_CHUNK_SIZE;

    // If remainder is 0 or if it's >= 5MB, we can use the minimum chunk size.
    if (remainder === 0 || remainder >= MIN_CHUNK_SIZE) {
      return {
        chunkSize: MIN_CHUNK_SIZE,
        partsCount: Math.ceil(fileSize / MIN_CHUNK_SIZE),
      };
    }

    {
      /*
      If remainder exists and is < 5MB, we need to redistribute the file size
      We are reducing the total parts for the case of file size < 500MB, in order to maintain minimum 5MB threshold of S3.
    */
    }
    const partsCount = completeChunks;
    const chunkSize = Math.ceil(fileSize / partsCount);

    return {
      chunkSize,
      partsCount,
    };
  }

  // For files greater than 500MB, use a fixed 20MB chunk size.
  let partsCount = Math.ceil(fileSize / LARGE_FILE_CHUNK_SIZE);
  let chunkSize = LARGE_FILE_CHUNK_SIZE;

  // If we exceed S3's 10,000 part limit, adjust the chunk size accordingly.
  if (partsCount > MAX_PARTS) {
    partsCount = MAX_PARTS;
    chunkSize = Math.ceil(fileSize / MAX_PARTS);
  }

  // Handle the case where the last chunk might be less than 5MB.
  const remainingBytes = fileSize % chunkSize;
  if (remainingBytes > 0 && remainingBytes < MIN_CHUNK_SIZE && partsCount > 1) {
    // Reduce the number of parts to ensure the last chunk is at least 5MB.
    partsCount = Math.floor(fileSize / chunkSize);
    chunkSize = Math.ceil(fileSize / partsCount);
  }

  return {
    chunkSize,
    partsCount,
  };
};

/**
 * Removes an upload identifier from sessionStorage
 * @param identifierToRemove The upload identifier to remove from storage
 */
export const removeUploadIdFromStorage = (
  identifierToRemove: UploadIdentifier,
) => {
  const uploadIds = sessionStorage.getItem('uploadIds');
  if (uploadIds) {
    const uploadIdsArray: UploadIdentifier[] = JSON.parse(uploadIds);
    const newUploadIdsArray = uploadIdsArray.filter(
      (identifier) => identifier.uploadId !== identifierToRemove.uploadId,
    );
    sessionStorage.setItem('uploadIds', JSON.stringify(newUploadIdsArray));
  }
};

/**
 * Stores an upload identifier in sessionStorage
 * @param identifierToStore The upload identifier to store
 */
export const storeUploadIdInStorage = (identifierToStore: UploadIdentifier) => {
  const uploadIds = sessionStorage.getItem('uploadIds');
  if (uploadIds) {
    const uploadIdsArray: UploadIdentifier[] = JSON.parse(uploadIds);
    // Check if the upload ID already exists in storage
    const isUploadIdExists = uploadIdsArray.some(
      (identifier) => identifier.uploadId === identifierToStore.uploadId,
    );

    if (!isUploadIdExists) {
      uploadIdsArray.push(identifierToStore);
      sessionStorage.setItem('uploadIds', JSON.stringify(uploadIdsArray));
    }
  } else {
    // Initialize with the first upload identifier if none exists
    sessionStorage.setItem('uploadIds', JSON.stringify([identifierToStore]));
  }
};

/**
 * Get all upload identifiers from sessionStorage
 * @returns An array of upload identifiers
 */
export const getAllUploadIds = (): UploadIdentifier[] => {
  const uploadIds = sessionStorage.getItem('uploadIds');
  if (uploadIds) {
    return JSON.parse(uploadIds);
  }
  return [];
};

/**
 * Extracts fileId from a completeUploadUrl path
 * @param url The complete upload URL containing the fileId
 * @returns The extracted fileId or empty string if not found
 */
export const extractFileIdFromUrl = (url: string): string => {
  // Use a regex to match the UUID pattern between /files/ and /complete-upload/
  const matches = url.match(/\/files\/([^/]+)\/complete-upload\//);

  // Return the extracted UUID or empty string if not found
  return matches && matches[1] ? matches[1] : '';
};

/**
 * Cancels all pending file uploads by calling completeUpload with dummy data
 * @param completeUploadFn - Function to call completeUpload API
 */
export const cancelPendingUploads = (
  completeUploadFn: CompleteUploadFunction,
) => {
  const uploadIds = getAllUploadIds();
  if (!isEmpty(uploadIds)) {
    // we call the completeUpload with wrong data to cancel the upload
    const dummyData = [{ ETag: '1', PartNumber: 1 }];
    uploadIds.forEach(({ fileId, uploadId }) => {
      completeUploadFn({
        upload_id: uploadId,
        fileId: fileId,
        filetype: FileType.IterationFile,
        parts: dummyData,
      });
      removeUploadIdFromStorage({ uploadId, fileId });
    });
  }
};
